import dayjs from 'dayjs';
import type { CoverageRunDto, UploadDto } from '@flowqa/shared';

export type CoverageArtifactRow = {
  id: string;
  artifactId: string;
  uploadId: string;
  coverageRunId: string;
  projectId: string;
  fileName: string;
  tags: string[];
  createdAt: Date;
  uploadCreatedAt: Date;
  uploadCapturedAt: Date | null;
  uploadedBy: string;
  sourceKey: string | null;
  uploadFileName: string | null;
  linesCovered: number | null;
  linesTotal: number | null;
  linesPct: number | null;
  functionsCovered: number | null;
  functionsTotal: number | null;
  functionsPct: number | null;
  branchesCovered: number | null;
  branchesTotal: number | null;
  branchesPct: number | null;
  statementsCovered: number | null;
  statementsTotal: number | null;
  statementsPct: number | null;
};

const getUploadArtifacts = (upload: UploadDto): UploadDto['artifacts'] => {
  const maybeArtifacts = (upload as { artifacts?: UploadDto['artifacts'] }).artifacts;
  return Array.isArray(maybeArtifacts) ? maybeArtifacts : [];
};

export const buildCoverageArtifactRows = (
  uploads: UploadDto[],
  runs: CoverageRunDto[],
): CoverageArtifactRow[] => {
  const latestRunByUploadId = new Map<string, CoverageRunDto>();

  [...runs]
    .sort(
      (left, right) =>
        new Date(right.capturedAt ?? right.createdAt).getTime() -
        new Date(left.capturedAt ?? left.createdAt).getTime(),
    )
    .forEach((run) => {
      if (!latestRunByUploadId.has(run.uploadId)) {
        latestRunByUploadId.set(run.uploadId, run);
      }
    });

  return uploads.flatMap((upload) => {
    const artifacts = getUploadArtifacts(upload);
    const run = latestRunByUploadId.get(upload.id) ?? null;

    if (!run) {
      return [];
    }

    if (artifacts.length === 0) {
      return [
        {
          id: `${upload.id}:upload`,
          artifactId: upload.id,
          uploadId: upload.id,
          coverageRunId: run.id,
          projectId: upload.projectId,
          fileName: upload.fileName,
          tags: upload.tags ?? [],
          createdAt: upload.createdAt,
          uploadCreatedAt: upload.createdAt,
          uploadCapturedAt: upload.capturedAt ?? null,
          uploadedBy: upload.createdByEmail ?? upload.createdByName ?? '-',
          sourceKey: upload.sourceKey ?? null,
          uploadFileName: upload.fileName,
          linesCovered: run.linesCovered,
          linesTotal: run.linesTotal,
          linesPct: run.linesPct,
          functionsCovered: run.functionsCovered ?? null,
          functionsTotal: run.functionsTotal ?? null,
          functionsPct: run.functionsPct ?? null,
          branchesCovered: run.branchesCovered ?? null,
          branchesTotal: run.branchesTotal ?? null,
          branchesPct: run.branchesPct ?? null,
          statementsCovered: run.statementsCovered ?? null,
          statementsTotal: run.statementsTotal ?? null,
          statementsPct: run.statementsPct ?? null,
        },
      ];
    }

    return artifacts.map((artifact) => ({
      id: `${upload.id}:${artifact.id}`,
      artifactId: artifact.id,
      uploadId: upload.id,
      coverageRunId: run.id,
      projectId: upload.projectId,
      fileName: artifact.fileName ?? upload.fileName,
      tags: upload.tags ?? [],
      createdAt: new Date(artifact.createdAt),
      uploadCreatedAt: upload.createdAt,
      uploadCapturedAt: upload.capturedAt ?? null,
      uploadedBy: upload.createdByEmail ?? upload.createdByName ?? '-',
      sourceKey: upload.sourceKey ?? null,
      uploadFileName: upload.fileName,
      linesCovered: run.linesCovered,
      linesTotal: run.linesTotal,
      linesPct: run.linesPct,
      functionsCovered: run.functionsCovered ?? null,
      functionsTotal: run.functionsTotal ?? null,
      functionsPct: run.functionsPct ?? null,
      branchesCovered: run.branchesCovered ?? null,
      branchesTotal: run.branchesTotal ?? null,
      branchesPct: run.branchesPct ?? null,
      statementsCovered: run.statementsCovered ?? null,
      statementsTotal: run.statementsTotal ?? null,
      statementsPct: run.statementsPct ?? null,
    }));
  });
};

export const buildCoverageTableDateRangeLabel = (
  startDay: string | null,
  endDay: string | null,
): string | null => {
  const startDate = startDay ? dayjs(startDay) : null;
  const endDate = endDay ? dayjs(endDay) : null;

  if (startDate && endDate) {
    return `${startDate.format('MMM D, YYYY')} - ${endDate.format('MMM D, YYYY')}`;
  }
  if (startDate) {
    return `From ${startDate.format('MMM D, YYYY')}`;
  }
  if (endDate) {
    return `To ${endDate.format('MMM D, YYYY')}`;
  }
  return null;
};
